<template>
  <div class="emails-manager">
    <!-- Sistema de Abas -->
    <div class="tabs-navigation">
      <button @click="activeTab = 'templates'" class="tab-nav-button"
        :class="{ 'tab-active': activeTab === 'templates' }">
        <i class="fa-solid fa-envelope"></i>
        <span>Templates</span>
      </button>
      <button @click="activeTab = 'campaigns'" class="tab-nav-button"
        :class="{ 'tab-active': activeTab === 'campaigns' }">
        <i class="fa-solid fa-paper-plane"></i>
        <span>Campanhas</span>
      </button>
      <button @click="activeTab = 'dashboard'" class="tab-nav-button"
        :class="{ 'tab-active': activeTab === 'dashboard' }">
        <i class="fa-solid fa-chart-line"></i>
        <span>Dashboard</span>
      </button>
      <button @click="activeTab = 'reports'" class="tab-nav-button"
        :class="{ 'tab-active': activeTab === 'reports' }">
        <i class="fa-solid fa-magnifying-glass"></i>
        <span>Consulta</span>
      </button>
    </div>

    <!-- Aba Templates -->
    <div v-show="activeTab === 'templates'" class="tab-content">
      <!-- Lista de templates -->
      <div v-if="!showEditor" class="templates-view">
        <div class="header-section modern-header">
          <div class="title-wrapper">
            <div class="icon-circle">
              <i class="fa-solid fa-envelope"></i>
            </div>
            <h2>Templates de Email</h2>
          </div>

          <div class="header-actions">
            <button @click="loadTemplates" class="btn btn-refresh" :disabled="loading">
              <i class="fa-solid fa-arrows-rotate"></i>
              {{ loading ? 'Carregando...' : 'Atualizar' }}
            </button>
            <button @click="createNewTemplate" class="btn btn-primary">
              <i class="fa-solid fa-circle-plus"></i>
              Criar Novo Template
            </button>
          </div>
        </div>
        <hr>
        <div class="filter-buttons">
          <div class="filter-label">
            <i class="fa-solid fa-filter"></i>
            <span>Filtrar</span>
          </div>

          <button @click="changeFilter('all')" class="btn btn-sm btn-primary">
            <i class="fa-solid fa-file"></i> Todos ({{ templates.length }})
          </button>
          <button @click="changeFilter('active')" class="btn btn-sm btn-success">
            <i class="fa-solid fa-lock-open"></i> Ativos ({{ activeCount }})
          </button>
          <button @click="changeFilter('inactive')" class="btn btn-sm btn-danger">
            <i class="fa-solid fa-lock"></i> Inativos ({{ inactiveCount }})
          </button>
        </div>

        <!-- Mensagens de erro -->
        <div v-if="errorMessage" class="alert alert-error">
          <span class="icon"><i class="fa-solid fa-triangle-exclamation"></i></span>
          {{ errorMessage }}
          <button @click="errorMessage = ''" class="alert-close"><i class="fa-solid fa-xmark"></i></button>
        </div>

        <!-- Mensagens de sucesso -->
        <div v-if="successMessage" class="alert alert-success">
          <span class="icon">✅</span>
          {{ successMessage }}
          <button @click="successMessage = ''" class="alert-close"><i class="fa-solid fa-xmark"></i></button>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Carregando templates do Brevo...</p>
        </div>

        <!-- Lista de templates -->
        <div v-else class="templates-grid">
          <div v-for="template in paginatedTemplates" :key="template.id" class="template-card">
            <div class="template-preview">
              <div v-if="getFirstImageUrl(template) && !imageErrorMap[template.id]" class="image-container">
                <div class="image-box">
                  <img :src="getFirstImageUrl(template)" :alt="template.name || 'Preview'" loading="lazy"
                    @error="handleImageError(template.id)" />
                </div>
              </div>

              <div v-else class="preview-placeholder">
                <span class="preview-icon"><i class="fa-solid fa-envelope-open"></i></span>
              </div>

              <div class="template-badge" :class="template.isActive ? 'badge-active' : 'badge-inactive'">
                <i :class="template.isActive ? 'fa-solid fa-lock-open' : 'fa-solid fa-lock'"></i>
                <span>{{ template.isActive ? 'Ativo' : 'Inativo' }}</span>
              </div>
            </div>

            <div class="template-info">
              <h3>{{ template.name }}</h3>
              <p class="template-subject" v-if="template.subject">
                <strong>Assunto:</strong> {{ template.subject }}
              </p>
              <p class="template-date">
                <strong>ID:</strong> {{ template.id }}<br>
                <strong>Criado:</strong> {{ formatDate(template.createdAt) }}<br>
                <strong>Modificado:</strong> {{ formatDate(template.modifiedAt) }}
              </p>
            </div>

            <div class="template-actions">
              <button @click="editTemplate(template.id)" class="btn btn-sm btn-primary" title="Editar"
                :disabled="loading">
                <i class="fa-solid fa-pen-to-square"></i>Editar
              </button>
              <button @click="toggleActive(template)" class="btn btn-sm"
                :class="template.isActive ? 'btn-warning' : 'btn-success'"
                :title="template.isActive ? 'Desativar' : 'Ativar'" :disabled="loading">
                <i v-if="template.isActive" class="fa-solid fa-lock"></i>
                <i v-else class="fa-solid fa-lock-open"></i>
                {{ template.isActive ? 'Desativar' : 'Ativar' }}
              </button>
              <button @click="duplicateTemplate(template)" class="btn btn-sm btn-secondary" title="Duplicar"
                :disabled="loading">
                <i class="fa-solid fa-clone"></i>Duplicar
              </button>
              <button @click="openDeleteModal(template.id, template.name)" class="btn btn-sm btn-danger" title="Excluir"
                :disabled="loading">
                <i class="fa-solid fa-trash"></i>Excluir
              </button>
            </div>
          </div>

          <!-- Empty state -->
          <div v-if="filteredTemplates.length === 0 && !loading" class="empty-state">
            <div class="empty-icon"><i class="fa-solid fa-envelope-open"></i></div>
            <h3>{{ getEmptyStateMessage() }}</h3>
            <p v-if="filterStatus === 'all'">Crie seu primeiro template de email clicando no botão acima</p>
            <p v-else>Tente alterar o filtro para ver outros templates</p>
          </div>
        </div>

        <!-- Paginação -->
        <div v-if="filteredTemplates.length > 0 && !loading" class="pagination-container">
          <div class="pagination-info">
            Mostrando {{ startIndex + 1 }} - {{ endIndex }} de {{ filteredTemplates.length }} templates
          </div>
          <div class="pagination-controls">
            <button @click="goToPage(1)" class="btn btn-secondary" :disabled="currentPage === 1"
              title="Primeira página">
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button @click="previousPage" class="btn btn-secondary" :disabled="currentPage === 1"
              title="Página anterior">
              <i class="fa-solid fa-chevron-left"></i>
            </button>

            <div class="pagination-pages">
              <button v-for="page in visiblePages" :key="page" @click="goToPage(page)" class="btn"
                :class="currentPage === page ? 'btn-primary' : 'btn-secondary'">
                {{ page }}
              </button>
            </div>

            <button @click="nextPage" class="btn btn-secondary" :disabled="currentPage === totalPages"
              title="Próxima página">
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button @click="goToPage(totalPages)" class="btn btn-secondary" :disabled="currentPage === totalPages"
              title="Última página">
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Editor -->
      <div v-else class="editor-view">
        <email-editor-brevo :template-id="currentTemplateId" :template-data="currentTemplateData" @close="closeEditor"
          @saved="handleTemplateSaved" />
      </div>

      <!-- Modal de confirmação de exclusão -->
      <div v-if="showDeleteModal" class="modal-overlay">
        <div class="modal-dialog">
          <div class="modal-header">
            <h3><i class="fa-solid fa-triangle-exclamation"></i> Confirmar Exclusão</h3>
            <button @click="showDeleteModal = false" class="modal-close-btn" type="button"><i
                class="fa-solid fa-xmark"></i></button>
          </div>
          <div class="modal-body">
            <p>Tem certeza que deseja excluir o template:</p>
            <p class="template-name-delete"><strong>{{ templateToDeleteName }}</strong></p>
            <p class="warning-text">Esta ação não pode ser desfeita.</p>
          </div>
          <div class="modal-footer">
            <button @click="showDeleteModal = false" class="btn btn-secondary" type="button">
              Cancelar
            </button>
            <button @click="handleDeleteClick" class="btn btn-danger" :disabled="deletingTemplate" type="button">
              {{ deletingTemplate ? 'Excluindo...' : 'Sim, Excluir' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Aba Campanhas -->
    <div v-show="activeTab === 'campaigns'" class="tab-content">
      <email-campaigns />
    </div>

    <!-- Aba Dashboard -->
    <div v-show="activeTab === 'dashboard'" class="full-width-tab">
      <email-dashboard />
    </div>

    <!-- Aba Relatórios -->
    <div v-show="activeTab === 'reports'" class="full-width-tab">
      <email-reports />
    </div>
  </div>
</template>

<script>
import EmailEditorBrevo from './EmailEditorBrevo.vue';
import EmailCampaigns from './EmailCampaigns.vue';
import EmailDashboard from './EmailDashboard.vue';
import EmailReports from './EmailReports.vue';

export default {
  name: 'Emails',

  components: {
    EmailEditorBrevo,
    EmailCampaigns,
    EmailDashboard,
    EmailReports
  },

  data() {
    return {
      activeTab: 'templates', // Inicia nos Templates
      imageErrorMap: {},
      imageLoadedMap: {},
      imagePreviewCache: {},
      templates: [],
      loading: false,
      showEditor: false,
      currentTemplateId: null,
      currentTemplateData: null,
      showDeleteModal: false,
      templateToDelete: null,
      templateToDeleteName: '',
      deletingTemplate: false,
      errorMessage: '',
      successMessage: '',
      filterStatus: 'all',
      currentPage: 1,
      itemsPerPage: 6
    };
  },

  computed: {
    filteredTemplates() {
      let filtered = [...this.templates];

      if (this.filterStatus === 'active') {
        filtered = filtered.filter(t => t.isActive);
      } else if (this.filterStatus === 'inactive') {
        filtered = filtered.filter(t => !t.isActive);
      }

      return filtered;
    },

    activeCount() {
      return this.templates.filter(t => t.isActive).length;
    },

    inactiveCount() {
      return this.templates.filter(t => !t.isActive).length;
    },

    totalPages() {
      return Math.ceil(this.filteredTemplates.length / this.itemsPerPage);
    },

    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },

    endIndex() {
      const end = this.startIndex + this.itemsPerPage;
      return end > this.filteredTemplates.length ? this.filteredTemplates.length : end;
    },

    paginatedTemplates() {
      return this.filteredTemplates.slice(this.startIndex, this.endIndex);
    },

    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;

      let start = Math.max(1, current - 2);
      let end = Math.min(total, current + 2);

      if (end - start < 4) {
        if (start === 1) {
          end = Math.min(total, start + 4);
        } else if (end === total) {
          start = Math.max(1, end - 4);
        }
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    }
  },

  mounted() {
    this.loadTemplates();
  },

  methods: {
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.scrollToTop();
      }
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.scrollToTop();
      }
    },

    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.scrollToTop();
      }
    },

    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    changeFilter(status) {
      this.filterStatus = status;
      this.currentPage = 1;
    },

    clearImageCache(templateId = null) {
      if (templateId) {
        const cacheKey = `id:${templateId}`;
        delete this.imagePreviewCache[cacheKey];
        delete this.imageErrorMap[templateId];
      } else {
        this.imagePreviewCache = {};
        this.imageErrorMap = {};
      }
    },

    getFirstImageUrl(template) {
      if (!template) return null;

      const cacheKey = template.id != null
        ? `id:${template.id}`
        : `html:${(template.htmlContent || '').slice(0, 200)}`;

      if (this.imagePreviewCache[cacheKey] !== undefined) {
        return this.imagePreviewCache[cacheKey];
      }

      let url = null;
      const html = template.htmlContent || '';
      if (!html) {
        this.imagePreviewCache[cacheKey] = null;
        return null;
      }

      try {
        if (typeof window !== 'undefined' && window.DOMParser) {
          const dp = new DOMParser();
          const doc = dp.parseFromString(html, 'text/html');

          const imgEls = Array.from(doc.querySelectorAll('img'));
          for (const imgEl of imgEls) {
            const src = imgEl.getAttribute('data-src') ||
              imgEl.getAttribute('data-original') ||
              imgEl.getAttribute('src') ||
              imgEl.src ||
              null;

            if (!src) continue;

            if (
              /play_circle|youtube|icon|button|material|gstatic/i.test(src) &&
              !/ytimg\.com/i.test(src)
            ) {
              continue;
            }

            url = src;
            break;
          }

          if (!url) {
            const meta = doc.querySelector('meta[property="og:image"], meta[name="twitter:image"]');
            if (meta) {
              url = meta.getAttribute('content') || null;
            }
          }
        }

        if (!url) {
          const ytMatch = html.match(
            /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{6,})/
          );
          if (ytMatch && ytMatch[1]) {
            url = `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`;
          }
        }

        if (!url) {
          const regex = /<img[^>]+(?:src|data-src|data-original)\s*=\s*["']?([^"'\s>]+)["']?/i;
          const m = html.match(regex);
          if (m && m[1]) url = m[1];
        }

        if (!url) {
          const bgMatch = html.match(/background(?:-image)?\s*:\s*url\((['"]?)(.*?)\1\)/i);
          if (bgMatch && bgMatch[2]) url = bgMatch[2];
        }

        if (url) {
          url = url.trim();

          if (/^javascript:/i.test(url)) url = null;
          if (url && url.startsWith('//') && typeof window !== 'undefined') {
            url = window.location.protocol + url;
          }
          if (url && url.startsWith('http:') && typeof window !== 'undefined' && window.location.protocol === 'https:') {
            url = url.replace(/^http:/, 'https:');
          }
        }
      } catch (e) {
        console.warn('Erro ao extrair imagem do htmlContent:', e);
        url = null;
      }

      this.$set(this.imagePreviewCache, cacheKey, url);
      return url;
    },

    handleImageError(templateId) {
      if (!templateId) return;
      this.$set(this.imageErrorMap, templateId, true);
    },

    handleImageLoad(templateId) {
      if (!templateId) return;
      this.$set(this.imageLoadedMap, templateId, true);
    },

    getSafePreviewHtml(html) {
      if (!html) return '<p style="color:#777;font-style:italic;">Sem conteúdo</p>';

      let safeHtml = html
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        .replace(/<link[^>]+>/gi, '');

      safeHtml = safeHtml.substring(0, 1500);

      const previewCss = `
      <style>
        body {
          font-family: Arial, sans-serif;
          color: #333;
          font-size: 13px;
          margin: 0;
          padding: 8px;
          background: white;
          overflow: hidden;
        }
        p, h1, h2, h3 {
          margin: 0 0 4px 0;
        }
        img { 
          max-width: 100%; 
          height: auto; 
        }
      </style>
    `;

      return `${previewCss}${safeHtml}`;
    },

    async loadTemplates() {
      this.loading = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const response = await fetch('/emails/api/brevo/templates', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          const clonedResponse = response.clone();
          let errorMessage;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || `Erro HTTP: ${response.status}`;
          } catch {
            try {
              const errorText = await clonedResponse.text();
              errorMessage = `Erro ${response.status}: Resposta inválida do servidor`;
            } catch {
              errorMessage = `Erro ${response.status}`;
            }
          }
          throw new Error(errorMessage);
        }

        const result = await response.json();

        if (result.success) {
          this.currentPage = 1;
          this.templates = result.data || [];
          this.imagePreviewCache = {};
          this.imageErrorMap = {};
        } else {
          throw new Error(result.message || 'Erro ao carregar templates');
        }
      } catch (error) {
        console.error('Erro ao carregar templates:', error);
        this.errorMessage = `Erro ao carregar templates: ${error.message}`;
      } finally {
        this.loading = false;
      }
    },

    async loadTemplateDetails(templateId) {
      try {
        const response = await fetch(`/emails/api/brevo/templates/${templateId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          const clonedResponse = response.clone();
          let errorMessage;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || `Erro HTTP: ${response.status}`;
          } catch {
            try {
              const errorText = await clonedResponse.text();
              errorMessage = `Erro ${response.status}: Resposta inválida do servidor`;
            } catch {
              errorMessage = `Erro ${response.status}`;
            }
          }
          throw new Error(errorMessage);
        }

        const result = await response.json();

        if (result.success) {
          return result.data;
        } else {
          throw new Error(result.message || 'Erro ao carregar template');
        }
      } catch (error) {
        console.error('Erro ao carregar detalhes do template:', error);
        this.errorMessage = `Erro ao carregar template: ${error.message}`;
        return null;
      }
    },

    createNewTemplate() {
      this.currentTemplateId = null;
      this.currentTemplateData = null;
      this.showEditor = true;
    },

    async editTemplate(templateId) {
      this.loading = true;
      this.errorMessage = '';

      const templateData = await this.loadTemplateDetails(templateId);

      if (templateData) {
        this.currentTemplateId = templateId;
        this.currentTemplateData = templateData;
        this.showEditor = true;
      }

      this.loading = false;
    },

    closeEditor() {
      this.showEditor = false;
      this.currentTemplateId = null;
      this.currentTemplateData = null;
      this.imagePreviewCache = {};
      this.imageErrorMap = {};
      this.loadTemplates();
    },

    handleTemplateSaved(message) {
      this.successMessage = message || 'Template salvo com sucesso!';
      if (this.currentTemplateId) {
        this.clearImageCache(this.currentTemplateId);
      }
      setTimeout(() => {
        this.successMessage = '';
      }, 5000);
    },

    async toggleActive(template) {
      this.loading = true;
      this.errorMessage = '';

      try {
        const newStatus = !template.isActive;

        const response = await fetch(`/emails/api/brevo/templates/${template.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify({
            isActive: newStatus
          })
        });

        if (!response.ok) {
          const clonedResponse = response.clone();
          let errorMessage;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || `Erro HTTP: ${response.status}`;
          } catch {
            try {
              const errorText = await clonedResponse.text();
              errorMessage = `Erro ${response.status}: Resposta inválida do servidor`;
            } catch {
              errorMessage = `Erro ${response.status}`;
            }
          }
          throw new Error(errorMessage);
        }

        const result = await response.json();

        if (result.success) {
          this.successMessage = `Template ${newStatus ? 'ativado' : 'desativado'} com sucesso!`;
          setTimeout(() => {
            this.successMessage = '';
          }, 5000);

          await this.loadTemplates();
        } else {
          throw new Error(result.message || 'Erro ao atualizar status');
        }
      } catch (error) {
        console.error('Erro ao atualizar status:', error);
        this.errorMessage = `Erro ao atualizar status: ${error.message}`;
      } finally {
        this.loading = false;
      }
    },

    async duplicateTemplate(template) {
      this.loading = true;
      this.errorMessage = '';

      try {
        const templateData = await this.loadTemplateDetails(template.id);

        if (!templateData) {
          throw new Error('Não foi possível carregar o template');
        }

        const duplicateData = {
          name: `${templateData.name} (Cópia)`,
          subject: templateData.subject,
          htmlContent: templateData.htmlContent,
          isActive: false
        };

        const response = await fetch('/emails/api/brevo/templates', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify(duplicateData)
        });

        if (!response.ok) {
          const clonedResponse = response.clone();
          let errorMessage;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || `Erro HTTP: ${response.status}`;
          } catch {
            try {
              const errorText = await clonedResponse.text();
              errorMessage = `Erro ${response.status}: Resposta inválida do servidor`;
            } catch {
              errorMessage = `Erro ${response.status}`;
            }
          }
          throw new Error(errorMessage);
        }

        const result = await response.json();

        if (result.success) {
          this.successMessage = 'Template duplicado com sucesso!';
          setTimeout(() => {
            this.successMessage = '';
          }, 5000);

          await this.loadTemplates();
        } else {
          throw new Error(result.message || 'Erro ao duplicar template');
        }
      } catch (error) {
        console.error('Erro ao duplicar template:', error);
        this.errorMessage = `Erro ao duplicar template: ${error.message}`;
      } finally {
        this.loading = false;
      }
    },

    openDeleteModal(templateId, templateName) {
      this.templateToDelete = templateId;
      this.templateToDeleteName = templateName;
      this.showDeleteModal = true;
    },

    handleDeleteClick() {
      this.confirmDelete();
    },

    async confirmDelete() {
      if (!this.templateToDelete) {
        return;
      }

      this.deletingTemplate = true;
      this.errorMessage = '';

      try {
        const url = `/emails/api/brevo/templates/${this.templateToDelete}`;

        const response = await fetch(url, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          const clonedResponse = response.clone();
          let errorMessage;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || `Erro HTTP: ${response.status}`;
          } catch {
            try {
              const errorText = await clonedResponse.text();
              errorMessage = `Erro ${response.status}: Resposta inválida do servidor`;
            } catch {
              errorMessage = `Erro ${response.status}`;
            }
          }
          throw new Error(errorMessage);
        }

        const result = await response.json();

        if (result.success) {
          this.successMessage = 'Template excluído com sucesso!';
          setTimeout(() => {
            this.successMessage = '';
          }, 5000);

          this.showDeleteModal = false;
          this.templateToDelete = null;
          this.templateToDeleteName = '';

          await this.loadTemplates();
        } else {
          throw new Error(result.message || 'Erro ao excluir template');
        }
      } catch (error) {
        console.error('Erro ao excluir template:', error);
        this.errorMessage = `Erro ao excluir template: ${error.message}`;
        this.showDeleteModal = false;
      } finally {
        this.deletingTemplate = false;
      }
    },

    getEmptyStateMessage() {
      if (this.filterStatus === 'active') {
        return 'Nenhum template ativo encontrado';
      } else if (this.filterStatus === 'inactive') {
        return 'Nenhum template inativo encontrado';
      }
      return 'Nenhum template encontrado';
    },

    onIframeLoad(event) {
      // Removido
    },

    formatDate(dateString) {
      if (!dateString) return '-';

      if (typeof dateString === 'string' && dateString.match(/^\d{2}\/\d{2}\/\d{4}/)) {
        return dateString;
      }

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Data não disponível';

        return date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch {
        return 'Data não disponível';
      }
    }
  }
};
</script>

<style scoped>
/* NOVO: Estilo para abas de tela cheia */
.full-width-tab {
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  padding: 0;
}

/* Estilo para abas normais (não dashboard) */
.tab-content {
  padding: 30px 20px;
}

.pagination-container {
  margin-top: 2rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.pagination-info {
  text-align: center;
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  font-weight: 500;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.pagination-pages {
  display: flex;
  gap: 0.25rem;
}

.pagination-controls .btn {
  min-width: 40px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.pagination-controls .btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-controls .btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  font-weight: 600;
}

/* Responsividade da paginação */
@media (max-width: 768px) {
  .pagination-pages {
    max-width: 100%;
    overflow-x: auto;
    padding: 0.25rem 0;
  }

  .pagination-controls {
    flex-direction: column;
    gap: 0.75rem;
  }
}

/* CSS das Abas - Versão Bonita */
.tabs-navigation {
  display: flex;
  border-bottom: 2px solid #e9ecef;
  background: #f8f9fa;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin: 0;
}

.tab-nav-button {
  flex: 1;
  padding: 18px 24px;
  border: none;
  background: transparent;
  font-size: 16px;
  font-weight: 600;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  position: relative;
  border-radius: 12px 12px 0 0;
}

.tab-nav-button:hover {
  background: #e9ecef;
  color: #495057;
}

.tab-nav-button.tab-active {
  color: #0d6efd;
  background: white;
}

.tab-nav-button.tab-active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 3px;
  background: #0d6efd;
}

.tab-nav-button i {
  font-size: 18px;
}

/* Responsividade das Abas */
@media (max-width: 768px) {
  .tabs-navigation {
    flex-direction: column;
  }

  .tab-nav-button {
    padding: 15px;
  }

  .tab-nav-button.tab-active::after {
    bottom: auto;
    right: -2px;
    left: auto;
    top: 0;
    bottom: 0;
    width: 3px;
    height: auto;
  }
}

/* TODO O SEU CSS EXISTENTE ABAIXO */
.filter-buttons {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #444;
  background: #ffffff;
  padding: 6px 12px;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
}

.filter-label i {
  color: #6c63ff;
  font-size: 14px;
}

.modern-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 2rem;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.modern-header:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
}

.title-wrapper {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.icon-circle {
  background: #3b82f6;
  color: #fff;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.title-wrapper h2 {
  font-size: 1.9rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.header-actions .btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 14px;
  font-weight: 500;
  padding: 0.6rem 1.2rem;
  border-radius: 12px;
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;
}

.btn i {
  font-size: 1.1rem;
}

.btn-refresh {
  background-color: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-refresh:hover {
  background-color: #e5e7eb;
  box-shadow: inset 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.template-preview {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  height: 140px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.image-container {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-box {
  background: #fff;
  border-radius: 6px;
  padding: 6px;
  width: 100%;
  height: 85%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.image-box img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.image-overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 8px 10px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.45) 100%);
  color: white;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.image-overlay .overlay-title {
  font-weight: 700;
  font-size: 14px;
  line-height: 1;
}

.image-overlay .overlay-sub {
  font-size: 12px;
  opacity: 0.95;
}

.emails-manager {
  min-height: 100vh;
  padding: 20px;
}

/* Padding padrão para abas normais */
.tab-content {
  padding: 20px;
  background: white;
  border-radius: 0 0 8px 8px;
}

.templates-view {
  max-width: 1400px;
  margin: 0 auto;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;
}

.header-section h2 {
  font-size: 28px;
  font-weight: 600;
  color: #333;
  margin: 0;
  flex: 1;
  min-width: 200px;
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.filter-buttons {
  display: flex;
  gap: 8px;
  padding: 5px;
  border-radius: 8px !important;
  justify-content: flex-end;
  margin-bottom: 30px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px !important;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-warning {
  background: #ffc107;
  color: #333;
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-refresh {
  display: flex;
  align-items: center;
  gap: 5px;
}

.alert {
  padding: 15px 20px;
  border-radius: 8px !important;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  animation: slideIn 0.3s ease;
  position: relative;
}

.alert-error {
  background-color: #fee;
  color: #c33;
  border: 1px solid #fcc;
}

.alert-success {
  background-color: #efe;
  color: #3c3;
  border: 1px solid #cfc;
}

.alert-close {
  margin-left: auto;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.alert-close:hover {
  opacity: 1;
}

@keyframes slideIn {
  from {
    transform: translateY(-10px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #667eea;
  border-radius: 50% !important;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.templates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  background-color: #ffffff;
  padding: 20px;
  border-radius: 16px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.templates-grid:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
}

.template-card {
  background: white;
  border-radius: 8px !important;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.template-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.template-preview {
  height: 200px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.preview-placeholder {
  font-size: 60px;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.template-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 12px;
  padding: 4px 10px;
  position: absolute;
  top: 8px;
  right: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.badge-active {
  background-color: #e6ffed;
  color: #1b8a3e;
  border: 1px solid #a7e1b8;
}

.badge-inactive {
  background-color: #ffecec;
  color: #b91c1c;
  border: 1px solid #f5b5b5;
}

.template-badge i {
  font-size: 13px;
}

.template-info {
  padding: 15px;
}

.template-info h3 {
  margin: 0 0 10px 0;
  font-size: 18px;
  color: #333;
  font-weight: 600;
}

.template-subject {
  margin: 8px 0;
  font-size: 13px;
  color: #555;
  line-height: 1.4;
}

.template-date {
  margin: 8px 0 0 0;
  font-size: 11px;
  color: #999;
  line-height: 1.6;
}

.template-actions {
  padding: 15px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 80px 20px;
}

.empty-icon {
  font-size: 80px;
  margin-bottom: 20px;
}

.empty-state h3 {
  font-size: 24px;
  color: #666;
  margin: 10px 0;
}

.empty-state p {
  font-size: 16px;
  color: #999;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-dialog {
  background: white;
  border-radius: 12px !important;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
  color: #333;
}

.modal-close-btn {
  position: relative;
  z-index: 10;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer !important;
  pointer-events: auto !important;
  color: #999;
  padding: 5px 10px;
}

.modal-close-btn:hover:not(:disabled) {
  color: #333;
  border-radius: 4px !important;
}

.modal-close-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-body {
  padding: 20px;
}

.modal-body p {
  margin: 10px 0;
  color: #666;
}

.template-name-delete {
  background: #f8f9fa;
  padding: 10px;
  border-radius: 5px !important;
  font-size: 16px;
  color: #333;
}

.warning-text {
  color: #dc3545;
  font-weight: 500;
  font-size: 14px;
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.modal-footer .btn {
  cursor: pointer !important;
  min-width: 120px;
  pointer-events: auto !important;
}

.icon {
  font-size: 16px;
}
</style>